import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, X, Save, Tags, Eye, EyeOff, KeyRound,
  Building2, Factory, Users, Truck, FlaskConical, MapPin, Calendar,
  Upload, Download, Search, ChevronUp, ChevronDown, ChevronsUpDown,
  Mail, RotateCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CrudTable, Modal, FormField } from '../components/SettingsComponents';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const emailListToText = (arr) => (arr || []).join(', ');
const emailTextToList = (str) => str.split(/[,\n]/).map(s => s.trim()).filter(Boolean);

// ─── EmailTagInput — chip-style email list input ───────────────────────────
function EmailTagInput({ value = [], onChange, placeholder = 'Type email and press Enter' }) {
  const [input, setInput] = React.useState('');

  const add = (raw) => {
    const emails = raw.split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes('@') && s.includes('.'));
    if (!emails.length) return;
    const next = [...new Set([...value, ...emails])];
    onChange(next);
    setInput('');
  };

  const remove = (email) => onChange(value.filter(e => e !== email));

  const handleKeyDown = (e) => {
    if (['Enter', ',', 'Tab'].includes(e.key)) {
      e.preventDefault();
      add(input);
    }
    if (e.key === 'Backspace' && !input && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    add(e.clipboardData.getData('text'));
  };

  return (
    <div
      className="min-h-[42px] border border-slate-300 rounded-md px-2 py-1.5 flex flex-wrap gap-1.5 focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500 bg-white cursor-text"
      onClick={e => e.currentTarget.querySelector('input')?.focus()}
    >
      {value.map(email => (
        <span key={email} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-0.5 text-xs font-mono">
          {email}
          <button type="button" onClick={() => remove(email)} className="text-blue-300 hover:text-red-500 leading-none ml-0.5 font-bold">×</button>
        </span>
      ))}
      <input
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (input.trim()) add(input); }}
        onPaste={handlePaste}
        placeholder={value.length === 0 ? placeholder : 'Add another email...'}
        className="flex-1 min-w-[180px] outline-none text-sm bg-transparent py-0.5"
      />
    </div>
  );
}

// ─── EmployeeListInput — row-by-row name + email ──────────────────────────
function EmployeeListInput({ value = [], onChange }) {
  const add = () => onChange([...value, { name: '', email: '' }]);
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const update = (i, field, val) => {
    const next = [...value];
    next[i] = { ...next[i], [field]: val };
    onChange(next);
  };

  return (
    <div className="space-y-2">
      {value.length === 0 && (
        <p className="text-xs text-slate-400 italic">No employees yet. Click "+ Add" below.</p>
      )}
      {value.map((emp, i) => (
        <div key={i} className="flex gap-2 items-center">
          <input
            value={emp.name || ''}
            onChange={e => update(i, 'name', e.target.value)}
            placeholder="Name (optional)"
            className="w-36 shrink-0 border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <input
            value={emp.email || ''}
            onChange={e => update(i, 'email', e.target.value)}
            placeholder="email@mill.com"
            type="email"
            className="flex-1 border border-slate-300 rounded-md px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button type="button" onClick={() => remove(i)} className="p-1 text-slate-300 hover:text-red-500 transition-colors shrink-0">
            <X size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={add} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1">
        <Plus size={12} /> Add employee
      </button>
    </div>
  );
}

// ─── Email Templates Tab ───────────────────────────────────────────────────────

// Default plain-English body for each template type (shown when no custom text saved yet)
const DEFAULT_PLAIN_BODIES = {
  new_order: `Dear Mill Team,

Please find attached the Delivery Order {do_number} dated {date}.

Party: {bill_to_name}
Mill: {mill_name}
Total Qty: {total_qty}

Kindly acknowledge receipt and process accordingly.

Regards,
{sender_name}
Phone: {sender_phone} | Fax: {sender_fax}
Email: {sender_email}

Attachment: {attachment_list}
Sent: {send_datetime}`,

  correction: `Dear Mill Team,

Please note that Delivery Order {do_number} has been updated.

The revised Delivery Order is attached to this email. Please discard the previous version and use this updated copy.

Regards,
{sender_name}
Phone: {sender_phone} | Fax: {sender_fax}
Email: {sender_email}

Attachment: {attachment_list}
Sent: {send_datetime}`,

  cancellation: `Dear Mill Team,

Delivery Order {do_number} has been cancelled by {cancelled_by}.

No further action is required on this order. Please update your records accordingly.

Regards,
{sender_name}
Phone: {sender_phone} | Fax: {sender_fax}
Email: {sender_email}

Sent: {send_datetime}`,

  credit_alert: `Dear Team,

Please be informed that Delivery Order {do_number} for {bill_to_name} has been marked as a credit order.

Kindly update your records and take necessary action.

Regards,
{sender_name}
Phone: {sender_phone} | Fax: {sender_fax}
Email: {sender_email}

Sent: {send_datetime}`,

  payment_undertaking: `Dear Mill Team,

Please find the Payment Undertaking for Delivery Order {do_number} dated {date}.

Party: {bill_to_name}

Kindly acknowledge and update your records.

Regards,
{sender_name}
Phone: {sender_phone} | Fax: {sender_fax}
Email: {sender_email}

Sent: {send_datetime}`,
};

// Which template types have DO PDF auto-attached
const PDF_ATTACHED_TYPES = new Set(['new_order', 'correction']);

// Friendly variable labels for insert buttons
const VARIABLE_LABELS = {
  do_number:      'Order #',
  date:           'Date',
  bill_to_name:   'Party Name',
  mill_name:      'Mill Name',
  total_qty:      'Total Qty',
  cancelled_by:   'Cancelled By',
  created_by_line:'Created By',
  sender_name:    'Sender Name',
  sender_phone:   'Sender Phone',
  sender_fax:     'Sender Fax',
  sender_email:   'Sender Email',
  attachment_list:'Attachment',
  send_datetime:  'Sent Date/Time',
};

function EmailTemplatesTab() {
  const [templates, setTemplates]   = useState([]);
  const [selected, setSelected]     = useState(null);
  const [subject, setSubject]       = useState('');
  const [bodyText, setBodyText]     = useState('');
  const [preview, setPreview]       = useState(false);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [resetting, setResetting]   = useState(false);
  const bodyRef  = useRef(null);
  const subjRef  = useRef(null);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/templates/email-templates/');
      setTemplates(res.data || []);
      if (res.data?.length > 0 && !selected) {
        pickTemplate(res.data[0], true);
      }
    } catch { toast.error('Failed to load email templates'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTemplates(); }, []);

  const pickTemplate = (tpl, force = false) => {
    if (!force && selected?.type === tpl.type) return;
    setSelected(tpl);
    setSubject(tpl.subject || '');
    setBodyText(tpl.plain_body || DEFAULT_PLAIN_BODIES[tpl.type] || '');
    setPreview(false);
  };

  // Insert a variable token at cursor position in the focused textarea/input
  const insertVar = (varKey, targetRef) => {
    const el = targetRef?.current;
    if (!el) return;
    const token = `{${varKey}}`;
    const start = el.selectionStart ?? bodyText.length;
    const end   = el.selectionEnd   ?? bodyText.length;
    if (el === bodyRef.current) {
      const next = bodyText.slice(0, start) + token + bodyText.slice(end);
      setBodyText(next);
    } else {
      const next = subject.slice(0, start) + token + subject.slice(end);
      setSubject(next);
    }
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; }, 0);
  };

  const [lastFocused, setLastFocused] = useState('body'); // 'body' | 'subject'

  const handleInsertVar = (varKey) => {
    if (lastFocused === 'subject') {
      insertVar(varKey, subjRef);
    } else {
      insertVar(varKey, bodyRef);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/templates/email-templates/${selected.type}`, {
        subject,
        plain_body: bodyText,
      });
      toast.success('Email template saved!');
      const res = await api.get('/templates/email-templates/');
      setTemplates(res.data || []);
      const refreshed = (res.data || []).find(t => t.type === selected.type);
      if (refreshed) {
        setSelected({ ...refreshed, plain_body: bodyText });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!selected) return;
    if (!window.confirm(`Reset "${selected.label}" to the built-in default? Your changes will be lost.`)) return;
    setResetting(true);
    try {
      await api.delete(`/templates/email-templates/${selected.type}`);
      toast.success('Reset to default');
      const res = await api.get('/templates/email-templates/');
      setTemplates(res.data || []);
      const refreshed = (res.data || []).find(t => t.type === selected.type);
      if (refreshed) pickTemplate({ ...refreshed, plain_body: null }, true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
    finally { setResetting(false); }
  };

  // Build a simple preview HTML from the plain text body
  const buildPreviewHtml = () => {
    if (!bodyText.trim()) return '<p style="color:#aaa;padding:16px">No content yet.</p>';
    const paragraphs = bodyText.trim().split('\n\n');
    const html = paragraphs.map(p =>
      `<p style="margin:0 0 14px 0;line-height:1.7;color:#374151">${p.replace(/\n/g, '<br>')}</p>`
    ).join('\n');
    return `<html><body style="font-family:Arial,sans-serif;padding:16px;background:#f3f4f6">
<div style="max-width:560px;margin:0 auto;background:#fff;padding:28px;border-radius:8px;border:1px solid #e5e7eb">
<div style="font-size:12px;color:#6b7280;border-bottom:1px solid #f3f4f6;padding-bottom:10px;margin-bottom:18px">
  <strong>Subject:</strong> ${subject || '(no subject)'}
</div>
${html}
${PDF_ATTACHED_TYPES.has(selected?.type) ? '<div style="margin-top:18px;padding:10px 12px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;font-size:11px;color:#16a34a">📎 Delivery Order PDF is automatically attached</div>' : ''}
</div></body></html>`;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex gap-4" data-testid="email-templates-tab">
      {/* Left: template list */}
      <div className="w-52 shrink-0 bg-white border border-slate-200 rounded-lg overflow-hidden self-start">
        <div className="px-3 py-2.5 border-b border-slate-100 bg-slate-50">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Template Types</p>
        </div>
        <div className="divide-y divide-slate-50">
          {templates.map(tpl => (
            <button
              key={tpl.type}
              onClick={() => pickTemplate(tpl)}
              data-testid={`email-tpl-${tpl.type}`}
              className={`w-full text-left px-3 py-2.5 transition-colors hover:bg-slate-50 ${selected?.type === tpl.type ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
            >
              <div className="flex items-center justify-between gap-1">
                <p className="text-sm font-medium text-slate-700 truncate">{tpl.label}</p>
                {PDF_ATTACHED_TYPES.has(tpl.type) && (
                  <span className="text-[9px] bg-green-50 text-green-600 border border-green-100 px-1.5 py-0.5 rounded-full shrink-0">PDF</span>
                )}
              </div>
              {tpl.is_custom && (
                <span className="text-[10px] text-blue-600 font-medium">Customized</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Right: plain-English editor */}
      {selected ? (
        <div className="flex-1 bg-white border border-slate-200 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-blue-600 shrink-0" />
              <span className="text-sm font-semibold text-slate-800">{selected.label}</span>
              {PDF_ATTACHED_TYPES.has(selected.type) && (
                <span className="text-xs bg-green-50 text-green-700 border border-green-100 px-2 py-0.5 rounded-full">
                  DO PDF auto-attached
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPreview(p => !p)}
                className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border transition-colors ${preview ? 'bg-slate-200 text-slate-700 border-slate-300' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}
                data-testid="toggle-preview-btn"
              >
                <Eye size={12} />
                {preview ? 'Back to Edit' : 'Preview Email'}
              </button>
              {selected.is_custom && (
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 transition-colors disabled:opacity-50"
                  data-testid="reset-template-btn"
                >
                  <RotateCcw size={11} />
                  {resetting ? 'Resetting…' : 'Reset to Default'}
                </button>
              )}
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1.5 h-8"
                data-testid="save-email-template-btn"
              >
                <Save size={12} />
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>

          {preview ? (
            /* Preview pane */
            <div className="p-4">
              <p className="text-xs text-slate-400 mb-2 flex items-center gap-1"><Eye size={11}/> Email Preview — this is how your email will look</p>
              <iframe
                title="email-preview"
                srcDoc={buildPreviewHtml()}
                className="w-full border border-slate-200 rounded-lg"
                style={{ height: 480 }}
                sandbox="allow-same-origin"
                data-testid="email-preview-panel"
              />
            </div>
          ) : (
            /* Edit pane */
            <div className="p-4 space-y-4">
              {/* Subject */}
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">
                  Subject Line
                </label>
                <Input
                  ref={subjRef}
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  onFocus={() => setLastFocused('subject')}
                  placeholder="e.g. Delivery Order {do_number} — Please Find Attached"
                  className="text-sm"
                  data-testid="email-subject-input"
                />
              </div>

              {/* Variable insert row */}
              <div>
                <p className="text-[10px] text-slate-400 mb-1.5 font-medium uppercase tracking-wider">
                  Insert into {lastFocused === 'subject' ? 'subject' : 'body'} — click to add:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {(selected.variables || []).map(v => (
                    <button
                      key={v}
                      onClick={() => handleInsertVar(v)}
                      title={`Insert {${v}}`}
                      className="text-[11px] px-2 py-1 bg-blue-50 text-blue-700 border border-blue-100 rounded-md hover:bg-blue-100 transition-colors font-medium"
                      data-testid={`insert-var-${v}`}
                    >
                      {VARIABLE_LABELS[v] || v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Body textarea */}
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1.5">
                  Email Body
                  <span className="ml-1.5 font-normal text-slate-400">— write in plain English, no HTML needed</span>
                </label>
                <textarea
                  ref={bodyRef}
                  value={bodyText}
                  onChange={e => setBodyText(e.target.value)}
                  onFocus={() => setLastFocused('body')}
                  rows={16}
                  spellCheck
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-y leading-relaxed"
                  placeholder="Write your email message here in plain English..."
                  data-testid="email-body-textarea"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Tip: Leave a blank line between paragraphs. Use the buttons above to insert order details automatically.
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 bg-white border border-slate-200 rounded-lg flex items-center justify-center h-48">
          <p className="text-slate-400 text-sm">Select a template type on the left</p>
        </div>
      )}
    </div>
  );
}

// ─── States Manager Component ─────────────────────────────────────────────────
function StatesManager({ states, onRefresh, isSuperAdmin, isAdmin }) {
  const [newStateName, setNewStateName] = useState('');
  const [addingState, setAddingState] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [pincodes, setPincodes] = useState([]);
  const [newPincode, setNewPincode] = useState('');
  const [addingPincode, setAddingPincode] = useState(false);

  const loadPincodes = async (state) => {
    setSelectedState(state);
    try {
      const res = await api.get(`/locations/pincodes/by-state/${state.state_id}`);
      setPincodes(res.data || []);
    } catch { setPincodes([]); }
  };

  const handleAddState = async () => {
    if (!newStateName.trim()) return;
    setAddingState(true);
    try {
      await api.post('/locations/states', { state_id: '', name: newStateName.trim() });
      setNewStateName('');
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'Error adding state'); }
    finally { setAddingState(false); }
  };

  const handleDeleteState = async (stateId) => {
    try {
      await api.delete(`/locations/states/${stateId}`);
      if (selectedState?.state_id === stateId) { setSelectedState(null); setPincodes([]); }
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleAddPincode = async () => {
    if (!newPincode.trim() || !selectedState) return;
    setAddingPincode(true);
    try {
      await api.post('/locations/pincodes', { pincode_id: '', code: newPincode.trim(), state_id: selectedState.state_id });
      setNewPincode('');
      await loadPincodes(selectedState);
    } catch { alert('Error adding pincode'); }
    finally { setAddingPincode(false); }
  };

  const handleDeletePincode = async (pincodeId) => {
    try {
      await api.delete(`/locations/pincodes/${pincodeId}`);
      if (selectedState) await loadPincodes(selectedState);
    } catch { alert('Error'); }
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">States ({states.length})</h3>
        </div>
        {isAdmin && (
          <div className="flex gap-2 px-4 py-3 border-b border-slate-100">
            <Input placeholder="New state name..." value={newStateName}
              onChange={e => setNewStateName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddState()}
              className="text-sm flex-1" data-testid="new-state-input" />
            <Button size="sm" onClick={handleAddState} disabled={addingState || !newStateName.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-state-btn">
              {addingState ? '...' : <Plus size={14} />}
            </Button>
          </div>
        )}
        <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-50">
          {states.map(s => (
            <div key={s.state_id} onClick={() => loadPincodes(s)}
              className={`flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-slate-50 transition-colors
                ${selectedState?.state_id === s.state_id ? 'bg-blue-50 border-l-2 border-blue-500' : ''}`}
              data-testid={`state-row-${s.state_id}`}>
              <span className="text-sm text-slate-700">{s.name}</span>
              <div className="flex items-center gap-1">
                {selectedState?.state_id === s.state_id && (
                  <span className="text-xs text-blue-600 font-medium">{pincodes.length} pins</span>
                )}
                {isSuperAdmin && (
                  <button onClick={e => { e.stopPropagation(); handleDeleteState(s.state_id); }}
                    className="p-1 text-slate-300 hover:text-red-500 rounded" data-testid={`delete-state-${s.state_id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold text-slate-700">
            {selectedState ? `Pincodes — ${selectedState.name}` : 'Select a state to manage pincodes'}
          </h3>
        </div>
        {selectedState ? (
          <>
            {isAdmin && (
              <div className="flex gap-2 px-4 py-3 border-b border-slate-100">
                <Input placeholder="e.g. 110001" value={newPincode}
                  onChange={e => setNewPincode(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddPincode()}
                  className="text-sm font-mono flex-1" maxLength={6} data-testid="new-pincode-input" />
                <Button size="sm" onClick={handleAddPincode} disabled={addingPincode || !newPincode.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-pincode-btn">
                  {addingPincode ? '...' : <Plus size={14} />}
                </Button>
              </div>
            )}
            <div className="max-h-[360px] overflow-y-auto">
              {pincodes.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-8">No pincodes added yet</p>
              ) : (
                <div className="flex flex-wrap gap-2 p-4">
                  {pincodes.map(p => (
                    <div key={p.pincode_id} className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 rounded-full border border-slate-200">
                      <span className="text-sm font-mono text-slate-700">{p.code}</span>
                      {isAdmin && (
                        <button onClick={() => handleDeletePincode(p.pincode_id)}
                          className="text-slate-300 hover:text-red-500 ml-0.5"><X size={11} /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-32">
            <p className="text-slate-400 text-sm">Click a state on the left to manage pincodes</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Settings Page ────────────────────────────────────────────────────────────
export default function Settings() {
  const { user } = useAuth();
  const isAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const [activeTab, setActiveTab] = useState('self_companies');
  const isInitialLoad = useRef(true);

  const [selfCompanies, setSelfCompanies] = useState([]);
  const [millGroups, setMillGroups] = useState([]);
  const [mills, setMills] = useState([]);
  const [parties, setParties] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [grades, setGrades] = useState([]);
  const [prodYears, setProdYears] = useState([]);
  const [states, setStates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [millGradesModal, setMillGradesModal] = useState(null);

  // Mill Prod Years assignment
  const [millProdYearsModal, setMillProdYearsModal] = useState(null); // mill object
  const [millPySelected, setMillPySelected] = useState(new Set()); // Set of prod_year_ids
  const [millPyDefault, setMillPyDefault] = useState(''); // prod_year_id
  const [millPySaving, setMillPySaving] = useState(false);

  // Parties search & sort
  const [partySearch, setPartySearch] = useState('');
  const [partySortKey, setPartySortKey] = useState('name');
  const [partySortDir, setPartySortDir] = useState('asc');

  // User management
  const [userModal, setUserModal] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', name: '', role: 'BROKER', password: '', phone_numbers: ['', '', '', '', ''] });
  const [savingUser, setSavingUser] = useState(false);
  const [userModalError, setUserModalError] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [phoneModal, setPhoneModal] = useState(null);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [pwModal, setPwModal] = useState(null);
  const [pwValue, setPwValue] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [showPw, setShowPw] = useState(false);

  // Excel import state
  const [importing, setImporting] = useState(null); // 'parties' | 'mills' | null
  const partiesFileRef = useRef(null);
  const millsFileRef = useRef(null);

  // Email diagnostics
  const [emailDiag, setEmailDiag] = useState(null);

  const loadAll = useCallback(async () => {
    if (isInitialLoad.current) setLoading(true);
    try {
      const reqs = [
        api.get('/masters/self-companies'),
        api.get('/masters/mill-groups'),
        api.get('/masters/mills'),
        api.get('/masters/parties'),
        api.get('/masters/trucks'),
        api.get('/masters/grades'),
        api.get('/masters/prod-years'),
        api.get('/locations/states'),
      ];
      if (isAdmin) reqs.push(api.get('/auth/users'));
      const results = await Promise.all(reqs);
      setSelfCompanies(results[0].data || []);
      setMillGroups(results[1].data || []);
      setMills(results[2].data || []);
      setParties(results[3].data || []);
      setTrucks(results[4].data || []);
      setGrades(results[5].data || []);
      setProdYears(results[6].data || []);
      setStates(results[7].data || []);
      if (isAdmin && results[8]) setUsers(results[8].data || []);
      // Load email diagnostics separately (non-blocking)
      if (isAdmin) {
        api.get('/masters/email-diagnostics').then(r => setEmailDiag(r.data)).catch(() => {});
      }
    } catch (e) { toast.error('Failed to load settings data. Please refresh the page.'); }
    finally {
      setLoading(false);
      isInitialLoad.current = false;
    }
  }, [isAdmin]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openModal = (type, item = {}) => { setModal({ type, item }); setForm({ ...item }); };
  const closeModal = () => { setModal(null); setForm({}); };

  const f = (key, defaultVal = '') => ({
    value: form[key] !== undefined ? form[key] : defaultVal,
    onChange: e => setForm(prev => ({ ...prev, [key]: e.target.value })),
    className: 'text-sm w-full',
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { type, item } = modal;

      if (type === 'self_company') {
        if (item.company_id) await api.put(`/masters/self-companies/${item.company_id}`, form);
        else await api.post('/masters/self-companies', form);
      } else if (type === 'mill_group') {
        if (item.mill_group_id) await api.put(`/masters/mill-groups/${item.mill_group_id}`, form);
        else await api.post('/masters/mill-groups', form);
      } else if (type === 'mill') {
        const millPayload = {
          ...form,
          sale_order_mail_list: form.sale_order_mail_list || [],
          credit_sales_list: form.credit_sales_list || [],
          payment_undertaking_mail_list: form.payment_undertaking_mail_list || [],
          mill_employees: form.mill_employees || [],
        };
        if (item.mill_id) await api.put(`/masters/mills/${item.mill_id}`, millPayload);
        else await api.post('/masters/mills', millPayload);
      } else if (type === 'party') {
        const payload = { ...form, phone_numbers: (form.phone_numbers || []).filter(p => p.trim()) };
        if (item.party_id) await api.put(`/masters/parties/${item.party_id}`, payload);
        else await api.post('/masters/parties', payload);
      } else if (type === 'truck') {
        if (item.truck_id) await api.put(`/masters/trucks/${item.truck_id}`, form);
        else await api.post('/masters/trucks', form);
      } else if (type === 'grade') {
        await api.post('/masters/grades', form);
      } else if (type === 'prod_year') {
        if (item.prod_year_id) await api.put(`/masters/prod-years/${item.prod_year_id}`, form);
        else await api.post('/masters/prod-years', form);
      }
      closeModal();
      loadAll();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving');
    } finally { setSaving(false); }
  };

  const handleDelete = async (collection, id) => {
    try {
      await api.delete(`/masters/${collection}/${id}`);
      loadAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error deleting'); }
  };

  // ─── Excel Import Handler ─────────────────────────────────────────────────
  const handleImport = async (type, file) => {
    if (!file) return;
    setImporting(type);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/masters/import/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { created, skipped, errors } = res.data;
      toast.success(`Imported ${created} ${type}, ${skipped} skipped`);
      if (errors.length > 0) {
        errors.slice(0, 3).forEach(e => toast.warning(e));
      }
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Import failed`);
    } finally {
      setImporting(null);
      if (type === 'parties' && partiesFileRef.current) partiesFileRef.current.value = '';
      if (type === 'mills' && millsFileRef.current) millsFileRef.current.value = '';
    }
  };

  const downloadTemplate = async (type) => {
    try {
      const res = await api.get(`/masters/template/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Could not download template'); }
  };

  // Mill Grades
  const handleMillGrades = async (mill) => {
    const res = await api.get(`/masters/mills/${mill.mill_id}/grades`);
    const sel = (res.data || []).map(g => g.grade_id);
    setMillGradesModal({ mill, selected: sel });
  };

  const saveMillGrades = async () => {
    if (!millGradesModal) return;
    setSaving(true);
    try {
      await api.put(`/masters/mills/${millGradesModal.mill.mill_id}/grades`, { grade_ids: millGradesModal.selected });
      setMillGradesModal(null);
    } catch { alert('Error saving grades'); }
    finally { setSaving(false); }
  };

  // Mill Prod Years assignment
  const openMillProdYearsModal = async (mill) => {
    try {
      const res = await api.get(`/masters/mills/${mill.mill_id}/assigned-prod-years`);
      const links = res.data || [];
      setMillPySelected(new Set(links.map(l => l.prod_year_id)));
      setMillPyDefault(links.find(l => l.is_default)?.prod_year_id || '');
    } catch { setMillPySelected(new Set()); setMillPyDefault(''); }
    setMillProdYearsModal(mill);
  };

  const saveMillProdYears = async () => {
    if (!millProdYearsModal) return;
    setMillPySaving(true);
    try {
      await api.put(`/masters/mills/${millProdYearsModal.mill_id}/assigned-prod-years`, {
        prod_year_ids: [...millPySelected],
        default_prod_year_id: millPyDefault || null,
      });
      setMillProdYearsModal(null);
    } catch { alert('Error saving prod years'); }
    finally { setMillPySaving(false); }
  };

  // User management
  const handleUserRole = async (userId, newRole) => {
    try { await api.patch(`/auth/users/${userId}/role`, { role: newRole }); loadAll(); }
    catch { alert('Error updating role'); }
  };

  const handleToggleActive = async (userId) => {
    try { await api.patch(`/auth/users/${userId}/active`); loadAll(); }
    catch { alert('Error'); }
  };

  const handleDeleteUser = async (userId) => {
    try { await api.delete(`/auth/users/${userId}`); loadAll(); }
    catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const handleAddUser = async () => {
    if (!userForm.email) { setUserModalError('Email is required'); return; }
    setUserModalError('');
    setSavingUser(true);
    try {
      const phone_numbers = userForm.phone_numbers.filter(p => p.trim() !== '');
      await api.post('/auth/users', { ...userForm, phone_numbers });
      setUserModal(false);
      setUserForm({ email: '', name: '', role: 'BROKER', password: '', phone_numbers: ['', '', '', '', ''] });
      loadAll();
    } catch (e) { setUserModalError(e.response?.data?.detail || 'Error'); }
    finally { setSavingUser(false); }
  };

  const handleSetPassword = async () => {
    if (!pwValue || pwValue.length < 6) { setPwError('Min 6 characters'); return; }
    setPwError('');
    setPwSaving(true);
    try {
      await api.patch(`/auth/users/${pwModal.user_id}/password`, { password: pwValue });
      setPwModal(null); setPwValue('');
    } catch (e) { setPwError(e.response?.data?.detail || 'Error'); }
    finally { setPwSaving(false); }
  };

  const handleSavePhones = async () => {
    if (!phoneModal) return;
    setPhoneSaving(true);
    try {
      const phone_numbers = phoneModal.phone_numbers.filter(p => p.trim() !== '');
      await api.patch(`/auth/users/${phoneModal.user_id}/phone-numbers`, { phone_numbers });
      setPhoneModal(null); loadAll();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
    finally { setPhoneSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Master Data & Configurations</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-lg flex flex-wrap gap-1 h-auto">
          {[
            { value: 'self_companies', label: 'Self Companies', Icon: Building2 },
            { value: 'mill_groups', label: 'Mill Groups', Icon: Factory },
            { value: 'mills', label: 'Mills', Icon: Factory },
            { value: 'parties', label: 'Parties', Icon: Users },
            { value: 'trucks', label: 'Trucks', Icon: Truck },
            { value: 'grades', label: 'Grades', Icon: FlaskConical },
            { value: 'prod_years', label: 'Prod. Years', Icon: Calendar },
            { value: 'states', label: 'States', Icon: MapPin },
            ...(isAdmin ? [{ value: 'users', label: 'Users', Icon: Users }] : []),
            ...(isAdmin ? [{ value: 'email_templates', label: 'Email Templates', Icon: Mail }] : []),
          ].map(({ value, label }) => (
            <TabsTrigger key={value} value={value}
              className="text-xs px-3 py-1.5 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Self Companies */}
        <TabsContent value="self_companies" className="mt-4 space-y-4">
          {/* Email Diagnostics Panel */}
          {emailDiag && (
            <div className={`rounded-lg border p-4 ${emailDiag.is_healthy ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-300'}`}
              data-testid="email-diagnostics-panel">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-800">
                  {emailDiag.is_healthy ? '✓ Email Configuration' : 'Email Configuration — Action Required'}
                </h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${emailDiag.is_healthy ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {emailDiag.is_healthy ? 'Healthy' : 'Needs Attention'}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs mb-3">
                <div>
                  <span className="text-slate-500">API Key</span>
                  <p className={`font-medium mt-0.5 ${emailDiag.resend_key_status === 'configured' ? 'text-green-700' : 'text-red-600'}`}>
                    {emailDiag.resend_key_status === 'configured' ? 'Configured' : emailDiag.resend_key_status === 'placeholder' ? 'Placeholder (not real)' : 'Not Set'}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">From Domain</span>
                  <p className={`font-medium mt-0.5 truncate ${emailDiag.is_test_domain ? 'text-amber-600' : 'text-green-700'}`}>
                    {emailDiag.from_domain || 'not set'}
                    {emailDiag.is_test_domain && ' (test only)'}
                  </p>
                </div>
                <div>
                  <span className="text-slate-500">CC Email</span>
                  <p className="font-medium text-slate-700 mt-0.5 truncate">{emailDiag.cc_email || 'None'}</p>
                </div>
              </div>
              {emailDiag.warnings.length > 0 && (
                <div className="space-y-1.5">
                  {emailDiag.warnings.map((w, i) => (
                    <div key={i} className="flex gap-2 text-xs text-amber-800 bg-amber-100 rounded px-2.5 py-1.5">
                      <span className="mt-0.5 shrink-0">!</span>
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
              {!emailDiag.is_healthy && (
                <p className="text-xs text-slate-500 mt-2">
                  Set these in <strong>Google Cloud Run Console</strong> → billmaster service → <strong>Edit &amp; Deploy New Revision</strong> → Variables &amp; Secrets.
                  Also verify your domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-blue-600 underline">resend.com/domains</a>.
                </p>
              )}
            </div>
          )}
          <CrudTable
            title="Self Companies"
            items={selfCompanies.map(c => ({ ...c, id: c.company_id }))}
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'nickname', label: 'Nickname' },
              { key: 'short_code', label: 'DO Prefix' },
              { key: 'gst', label: 'GSTIN' },
              { key: 'jurisdiction', label: 'Jurisdiction' },
            ]}
            canAdd={isAdmin} canEdit={isAdmin} canDelete={isSuperAdmin}
            onAdd={() => openModal('self_company', {})}
            onEdit={item => openModal('self_company', item)}
            onDelete={item => handleDelete('self-companies', item.company_id)}
          />
        </TabsContent>

        {/* Mill Groups */}
        <TabsContent value="mill_groups" className="mt-4">
          <CrudTable
            title="Mill Groups"
            items={millGroups.map(g => ({ ...g, id: g.mill_group_id }))}
            columns={[
              { key: 'name', label: 'Group Name' },
              { key: 'group_code', label: 'Group Code (DO)' },
            ]}
            canAdd={isAdmin} canEdit={isAdmin} canDelete={isSuperAdmin}
            onAdd={() => openModal('mill_group', {})}
            onEdit={item => openModal('mill_group', item)}
            onDelete={item => handleDelete('mill-groups', item.mill_group_id)}
          />
        </TabsContent>

        {/* Mills */}
        <TabsContent value="mills" className="mt-4">
          {isAdmin && (
            <div className="flex items-center gap-2 mb-3">
              <input ref={millsFileRef} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={e => { if (e.target.files[0]) handleImport('mills', e.target.files[0]); }}
                data-testid="mills-file-input" />
              <Button size="sm" variant="outline"
                className="text-xs border-slate-300 text-slate-600 hover:bg-slate-50"
                onClick={() => downloadTemplate('mills')}
                data-testid="download-mills-template-btn">
                <Download size={13} className="mr-1.5" /> Download Template
              </Button>
              <Button size="sm" variant="outline"
                className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                disabled={importing === 'mills'}
                onClick={() => millsFileRef.current?.click()}
                data-testid="import-mills-btn">
                <Upload size={13} className="mr-1.5" />
                {importing === 'mills' ? 'Importing...' : 'Import Excel'}
              </Button>
            </div>
          )}
          <CrudTable
            title="Mills / Factories"
            items={mills.map(m => ({ ...m, id: m.mill_id }))}
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'short_name', label: 'Short Name', render: item => item.short_name
                  ? <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-mono">{item.short_name}</span>
                  : <span className="text-slate-300 text-xs">—</span> },
              { key: 'unit_code', label: 'Unit Code' },
              {
                key: 'mill_group_id', label: 'Group',
                render: item => {
                  const mg = millGroups.find(g => g.mill_group_id === item.mill_group_id);
                  return mg ? <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">{mg.group_code}</span>
                    : <span className="text-slate-300 text-xs">—</span>;
                }
              },
              {
                key: 'assigned_self_company_id', label: 'Self Co.',
                render: item => {
                  const sc = selfCompanies.find(c => c.company_id === item.assigned_self_company_id);
                  return sc ? <span className="text-xs text-slate-700">{sc.short_code}</span>
                    : <span className="text-slate-300 text-xs">—</span>;
                }
              },
              {
                key: 'mill_employees', label: 'Employees',
                render: item => {
                  const count = (item.mill_employees || []).filter(e => e.name || typeof e === 'string').length;
                  return <span className="text-xs text-slate-500">{count > 0 ? `${count} emp.` : '—'}</span>;
                }
              },
            ]}
            canAdd={isAdmin} canEdit={isAdmin} canDelete={isSuperAdmin}
            onAdd={() => openModal('mill', { sale_order_mail_list: [], credit_sales_list: [], payment_undertaking_mail_list: [], mill_employees: [] })}
            onEdit={item => openModal('mill', {
              ...item,
              sale_order_mail_list: item.sale_order_mail_list || [],
              credit_sales_list: item.credit_sales_list || [],
              payment_undertaking_mail_list: item.payment_undertaking_mail_list || [],
              mill_employees: item.mill_employees || [],
            })}
            onDelete={item => handleDelete('mills', item.mill_id)}
            renderExtra={item => (
              <div className="flex items-center gap-1">
                <button onClick={() => handleMillGrades(item)}
                  className="p-1.5 text-slate-400 hover:text-orange-600 rounded" title="Manage Grades"
                  data-testid={`manage-grades-${item.mill_id}`}>
                  <Tags size={13} />
                </button>
                <button onClick={() => openMillProdYearsModal(item)}
                  className="p-1.5 text-slate-400 hover:text-blue-600 rounded" title="Assign Production Years"
                  data-testid={`manage-prod-years-${item.mill_id}`}>
                  <Calendar size={13} />
                </button>
              </div>
            )}
          />
        </TabsContent>

        {/* Parties */}
        <TabsContent value="parties" className="mt-4">
          {/* Import/Export toolbar */}
          {isAdmin && (
            <div className="flex items-center gap-2 mb-3">
              <input ref={partiesFileRef} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={e => { if (e.target.files[0]) handleImport('parties', e.target.files[0]); }}
                data-testid="parties-file-input" />
              <Button size="sm" variant="outline"
                className="text-xs border-slate-300 text-slate-600 hover:bg-slate-50"
                onClick={() => downloadTemplate('parties')}
                data-testid="download-parties-template-btn">
                <Download size={13} className="mr-1.5" /> Download Template
              </Button>
              <Button size="sm" variant="outline"
                className="text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
                disabled={importing === 'parties'}
                onClick={() => partiesFileRef.current?.click()}
                data-testid="import-parties-btn">
                <Upload size={13} className="mr-1.5" />
                {importing === 'parties' ? 'Importing...' : 'Import Excel'}
              </Button>
            </div>
          )}

          {/* Parties Table with Search + Sort */}
          {(() => {
            const PINCODE_RE = /\b[1-9]\d{5}\b/;

            const resolveState = (item) => {
              if (item.state_id) {
                const st = states.find(s => s.state_id === item.state_id);
                if (st) return st.name;
              }
              // Fallback: match any state name in address
              if (item.address) {
                const lower = item.address.toLowerCase();
                const found = states.find(s => lower.includes(s.name.toLowerCase()));
                if (found) return found.name;
              }
              return '';
            };

            const resolvePincode = (item) => {
              if (item.pincode) return item.pincode;
              if (item.address) {
                const m = item.address.match(PINCODE_RE);
                if (m) return m[0];
              }
              return '';
            };

            // Enrich parties with resolved state/pincode for search + sort
            const enriched = parties.map((p, idx) => ({
              ...p,
              _sno: idx + 1,
              _stateName: resolveState(p),
              _pincode: resolvePincode(p),
            }));

            // Global search across all fields
            const q = partySearch.trim().toLowerCase();
            const filtered = q
              ? enriched.filter(p =>
                  [p.name, p.nickname, p.address, p.district, p.gst, p._stateName, p._pincode,
                   ...(p.phone_numbers || [])]
                    .some(v => v && String(v).toLowerCase().includes(q))
                )
              : enriched;

            // Sort
            const sorted = [...filtered].sort((a, b) => {
              let av = a[partySortKey] ?? '';
              let bv = b[partySortKey] ?? '';
              // Numeric sort for S.No.
              if (partySortKey === '_sno') return partySortDir === 'asc' ? av - bv : bv - av;
              av = String(av).toLowerCase();
              bv = String(bv).toLowerCase();
              if (av < bv) return partySortDir === 'asc' ? -1 : 1;
              if (av > bv) return partySortDir === 'asc' ? 1 : -1;
              return 0;
            });

            const SortHeader = ({ label, sortKey }) => {
              const active = partySortKey === sortKey;
              const Icon = active
                ? (partySortDir === 'asc' ? ChevronUp : ChevronDown)
                : ChevronsUpDown;
              return (
                <th
                  className="cursor-pointer select-none hover:bg-slate-100 transition-colors"
                  onClick={() => {
                    if (partySortKey === sortKey) setPartySortDir(d => d === 'asc' ? 'desc' : 'asc');
                    else { setPartySortKey(sortKey); setPartySortDir('asc'); }
                  }}
                >
                  <span className="flex items-center gap-1">
                    {label} <Icon size={12} className={active ? 'text-blue-600' : 'text-slate-300'} />
                  </span>
                </th>
              );
            };

            return (
              <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {/* Header: title + search + add */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 gap-3 flex-wrap">
                  <h3 className="text-sm font-semibold text-slate-700 shrink-0">
                    Parties
                    <span className="ml-2 text-xs font-normal text-slate-400">
                      {filtered.length}{q ? ` of ${enriched.length}` : ''}
                    </span>
                  </h3>
                  <div className="relative flex-1 min-w-[200px] max-w-xs">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={partySearch}
                      onChange={e => setPartySearch(e.target.value)}
                      placeholder="Search name, GST, state, pincode..."
                      className="w-full pl-7 pr-3 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:border-blue-500"
                      data-testid="party-search-input"
                    />
                  </div>
                  <Button size="sm" onClick={() => openModal('party', { phone_numbers: ['', '', ''] })}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1 shrink-0">
                    <Plus size={13} /> Add
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full erp-table">
                    <thead>
                      <tr>
                        <SortHeader label="S.No." sortKey="_sno" />
                        <SortHeader label="Name" sortKey="name" />
                        <SortHeader label="Nickname" sortKey="nickname" />
                        <SortHeader label="District" sortKey="district" />
                        <SortHeader label="State" sortKey="_stateName" />
                        <SortHeader label="Pincode" sortKey="_pincode" />
                        <th>GSTIN</th>
                        <th>Phone(s)</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-slate-400 text-sm">
                            {q ? 'No parties match your search.' : 'No parties yet.'}
                          </td>
                        </tr>
                      ) : sorted.map((p) => (
                        <tr key={p.party_id} data-testid={`party-row-${p.party_id}`}>
                          <td className="text-xs text-slate-400 w-10 text-center">{p._sno}</td>
                          <td className="font-medium text-sm">{p.name || '—'}</td>
                          <td className="text-xs text-slate-500">{p.nickname || '—'}</td>
                          <td className="text-xs text-slate-600">{p.district || '—'}</td>
                          <td className="text-xs text-slate-600">
                            {p._stateName
                              ? <span>{p._stateName}{!p.state_id && <span className="ml-1 text-slate-300 text-[10px]">(addr)</span>}</span>
                              : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="text-xs font-mono text-slate-600">
                            {p._pincode
                              ? <span>{p._pincode}{!p.pincode && p._pincode !== p.pincode && <span className="ml-1 text-slate-300 text-[10px]">(addr)</span>}</span>
                              : <span className="text-slate-300">—</span>}
                          </td>
                          <td className="text-xs font-mono text-slate-500">{p.gst || '—'}</td>
                          <td className="text-xs text-slate-500">{(p.phone_numbers || []).filter(Boolean).join(', ') || '—'}</td>
                          <td>
                            <div className="flex items-center justify-end gap-1">
                              {isAdmin && (
                                <button onClick={() => openModal('party', { ...p, phone_numbers: [...(p.phone_numbers || []), '', '', ''].slice(0, 3) })}
                                  className="p-1.5 text-slate-400 hover:text-blue-600 rounded" data-testid={`edit-party-${p.party_id}`}>
                                  <Pencil size={13} />
                                </button>
                              )}
                              {isAdmin && (
                                <button onClick={() => handleDelete('parties', p.party_id)}
                                  className="p-1.5 text-slate-400 hover:text-red-600 rounded" data-testid={`delete-party-${p.party_id}`}>
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </TabsContent>

        {/* Trucks */}
        <TabsContent value="trucks" className="mt-4">
          <CrudTable
            title="Trucks"
            items={trucks.map(t => ({ ...t, id: t.truck_id }))}
            columns={[
              { key: 'reg_no', label: 'Reg. No.', className: 'font-mono font-medium' },
              { key: 'driver_name', label: 'Driver Name' },
            ]}
            canAdd canEdit={isAdmin} canDelete={isAdmin}
            onAdd={() => openModal('truck', {})}
            onEdit={item => openModal('truck', item)}
            onDelete={item => handleDelete('trucks', item.truck_id)}
          />
        </TabsContent>

        {/* Grades */}
        <TabsContent value="grades" className="mt-4">
          <CrudTable
            title="Grades"
            items={grades.map(g => ({ ...g, id: g.grade_id }))}
            columns={[
              { key: 'short_name', label: 'Short Name' },
              { key: 'name', label: 'Full Name' },
            ]}
            canAdd={isAdmin} canEdit={false} canDelete={isSuperAdmin}
            onAdd={() => openModal('grade', {})}
            onEdit={() => {}}
            onDelete={item => handleDelete('grades', item.grade_id)}
          />
        </TabsContent>

        {/* Production Years */}
        <TabsContent value="prod_years" className="mt-4">
          <CrudTable
            title="Production Years"
            items={prodYears.map(y => ({ ...y, id: y.prod_year_id }))}
            columns={[
              { key: 'sno', label: 'S.No.' },
              { key: 'prod_year', label: 'Production Year' },
            ]}
            canAdd={isAdmin} canEdit={isAdmin} canDelete={isSuperAdmin}
            onAdd={() => openModal('prod_year', {})}
            onEdit={item => openModal('prod_year', item)}
            onDelete={item => handleDelete('prod-years', item.prod_year_id)}
          />
        </TabsContent>

        {/* States */}
        <TabsContent value="states" className="mt-4">
          <StatesManager states={states} onRefresh={loadAll} isSuperAdmin={isSuperAdmin} isAdmin={isAdmin} />
        </TabsContent>

        {/* Users */}
        {isAdmin && (
          <TabsContent value="users" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700">User Management</h3>
                {isSuperAdmin && (
                  <Button size="sm" onClick={() => {
                    setUserForm({ email: '', name: '', role: 'BROKER', password: '', phone_numbers: ['', '', '', '', ''] });
                    setUserModalError('');
                    setUserModal(true);
                  }}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs gap-1" data-testid="add-user-btn">
                    <Plus size={13} /> Add User
                  </Button>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full erp-table" data-testid="users-table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th><th>Phone(s)</th><th>Role</th><th>Status</th>
                      {isAdmin && <th className="text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                        <td className="font-medium text-slate-800">{u.name}</td>
                        <td className="text-slate-500 text-xs">{u.email}</td>
                        <td className="text-xs text-slate-500">{(u.phone_numbers || []).filter(Boolean).join(', ') || '—'}</td>
                        <td>
                          {isSuperAdmin ? (
                            <select value={u.role} onChange={e => handleUserRole(u.user_id, e.target.value)}
                              className="border border-slate-200 rounded px-2 py-1 text-xs bg-white"
                              data-testid={`user-role-${u.user_id}`}>
                              <option value="SUPER_ADMIN">Super Admin</option>
                              <option value="ADMIN">Admin</option>
                              <option value="BROKER">Broker</option>
                            </select>
                          ) : <span className="text-xs font-medium text-slate-600">{u.role}</span>}
                        </td>
                        <td>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        {isAdmin && (
                          <td className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {isSuperAdmin && (
                                <>
                                  <button onClick={() => setPhoneModal({ user_id: u.user_id, name: u.name, phone_numbers: [...(u.phone_numbers || []), '', '', '', '', ''].slice(0, 5) })}
                                    className="p-1.5 text-slate-400 hover:text-green-600 rounded text-xs" title="Edit Phones"
                                    data-testid={`edit-phones-${u.user_id}`}>📞</button>
                                  <button onClick={() => { setPwModal({ user_id: u.user_id, name: u.name }); setPwValue(''); setPwError(''); }}
                                    className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                                    data-testid={`set-pw-${u.user_id}`} title="Set Password">
                                    <KeyRound size={13} />
                                  </button>
                                  <button onClick={() => handleToggleActive(u.user_id)}
                                    className="text-xs text-slate-500 hover:text-slate-800 border border-slate-200 rounded px-2 py-1"
                                    data-testid={`toggle-active-${u.user_id}`}>
                                    {u.is_active ? 'Deactivate' : 'Activate'}
                                  </button>
                                  <button onClick={() => handleDeleteUser(u.user_id)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                                    data-testid={`delete-user-${u.user_id}`} title="Delete">
                                    <Trash2 size={13} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-slate-400 text-sm">No users yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Email Templates */}
        {isAdmin && (
          <TabsContent value="email_templates" className="mt-4">
            <EmailTemplatesTab />
          </TabsContent>
        )}
      </Tabs>

      {/* ─── Generic Edit Modal ─────────────────────────────────── */}
      {modal && (
        <Modal
          title={`${modal.item.company_id || modal.item.mill_group_id || modal.item.mill_id || modal.item.party_id || modal.item.truck_id || modal.item.grade_id || modal.item.prod_year_id ? 'Edit' : 'New'} ${modal.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
          onClose={closeModal} onSave={handleSave} saving={saving}>

          {modal.type === 'self_company' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Company Name *"><Input {...f('name')} placeholder="Full legal name" /></FormField>
                <FormField label="Nickname"><Input {...f('nickname')} placeholder="Short name" /></FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Short Code (DO Prefix) *"><Input {...f('short_code')} placeholder="IPS" className="font-mono text-sm w-full" /></FormField>
                <FormField label="GSTIN"><Input {...f('gst')} placeholder="07AABCI1234F1Z5" className="font-mono text-sm w-full" /></FormField>
              </div>
              <FormField label="Address">
                <textarea value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                  rows={2} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
              </FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="State"><Input {...f('state')} placeholder="Delhi" /></FormField>
                <FormField label="Pincode"><Input {...f('pincode')} placeholder="110020" /></FormField>
              </div>
              <FormField label="Jurisdiction"><Input {...f('jurisdiction')} placeholder="Delhi" /></FormField>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Phone" hint="e.g. +91-11-23958567, 23958568">
                  <Input {...f('phone')} placeholder="+91-11-23958567" />
                </FormField>
                <FormField label="Fax" hint="e.g. +91-11-23965780">
                  <Input {...f('fax')} placeholder="+91-11-23965780" />
                </FormField>
              </div>
              <FormField label="Email ID"><Input {...f('email_id')} type="email" placeholder="info@company.com" /></FormField>
              <FormField label="From Email (subdomain sender)" hint="e.g. do.ips@sugarco.in — leave blank to use default">
                <Input {...f('from_email')} type="email" placeholder="do.ips@sugarco.in" />
              </FormField>
              <FormField label="Display Name (FROM label)" hint='Shown as sender name — e.g. "IPS Logistics". Result: IPS Logistics <do.ips@sugarco.in>'>
                <Input {...f('display_name')} placeholder="IPS Logistics" />
              </FormField>
              <FormField label="Reply-To Email" hint="Replies from mills go here">
                <Input {...f('reply_to')} type="email" placeholder="bajaj.ips@gmail.com" />
              </FormField>
              <FormField label="BCC Email" hint="All outgoing DOs will be silently copied here. Comma-separate multiple addresses.">
                <Input {...f('bcc_email')} placeholder="bajaj.ips@gmail.com, billing@sugarco.in" />
              </FormField>
              {modal.data?.company_id && (
                <div className="flex items-center gap-2 pt-1">
                  <button
                    type="button"
                    onClick={async () => {
                      const toEmail = form.email_id || '';
                      if (!toEmail) { toast.error('Set the Email ID field first'); return; }
                      try {
                        const res = await api.post('/masters/test-email', {
                          to_email: toEmail,
                          from_email: form.from_email || undefined,
                        });
                        if (res.data.warning) {
                          toast.warning(`Email sent but: ${res.data.warning}`, { duration: 8000 });
                        } else {
                          toast.success(`Test email sent to ${res.data.to}`);
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Test email failed — check RESEND_API_KEY & domain', { duration: 8000 });
                      }
                    }}
                    className="text-xs px-3 py-1.5 border border-blue-300 text-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                    data-testid="test-email-btn"
                  >
                    Send Test Email
                  </button>
                  <span className="text-xs text-slate-400">Sends to Email ID above — verifies Resend API key &amp; domain</span>
                </div>
              )}
              <FormField label="Signature (upload image)">
                {form.signature_base64 && <img src={form.signature_base64} alt="sig" className="max-h-16 border rounded mb-2" />}
                <Input type="file" accept="image/*" onChange={e => {
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onloadend = () => setForm(p => ({ ...p, signature_base64: reader.result }));
                  reader.readAsDataURL(file);
                }} className="text-xs" />
              </FormField>
            </div>
          )}

          {modal.type === 'mill_group' && (
            <>
              <FormField label="Group Name *"><Input {...f('name')} placeholder="Ambuja Cement Group" /></FormField>
              <FormField label="Group Code *">
                <Input {...f('group_code')} placeholder="AMB" className="font-mono text-sm w-full" />
                <p className="text-xs text-slate-400 mt-1">Used in DO number. E.g. 'AMB' → IPS--AMB--GGN--...</p>
              </FormField>
              <FormField label="Sale Order Email" hint="TO: for collective sale order summary mails">
                <Input {...f('sale_order_email')} type="email" placeholder="saleorder@millgroup.com" />
              </FormField>
              <FormField label="Credit Email" hint="TO: for collective credit summary mails">
                <Input {...f('credit_email')} type="email" placeholder="credit@millgroup.com" />
              </FormField>
              <FormField label="Payment Undertaking Email" hint="TO: for collective payment undertaking mails">
                <Input {...f('payment_undertaking_email')} type="email" placeholder="undertaking@millgroup.com" />
              </FormField>
            </>
          )}

          {modal.type === 'mill' && (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Mill Name *"><Input {...f('name')} /></FormField>
                <FormField label="Short Name" hint="Quick search abbreviation, e.g. BPL, MNS">
                  <Input {...f('short_name')} placeholder="BPL" className="font-mono text-sm w-full uppercase" />
                </FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Unit Code *"><Input {...f('unit_code')} placeholder="GGN" className="font-mono text-sm w-full" /></FormField>
                <FormField label="Mill Group">
                  <select value={form.mill_group_id || ''} onChange={e => setForm(p => ({ ...p, mill_group_id: e.target.value || null }))}
                    className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">— No Group —</option>
                    {millGroups.map(g => <option key={g.mill_group_id} value={g.mill_group_id}>{g.name} ({g.group_code})</option>)}
                  </select>
                </FormField>
                <FormField label="Self Company">
                  <select value={form.assigned_self_company_id || ''} onChange={e => setForm(p => ({ ...p, assigned_self_company_id: e.target.value || null }))}
                    className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">— None —</option>
                    {selfCompanies.map(c => <option key={c.company_id} value={c.company_id}>{c.name} ({c.short_code})</option>)}
                  </select>
                </FormField>
              </div>
              <FormField label="Address">
                <textarea value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                  rows={2} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
              </FormField>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="GSTIN"><Input {...f('gst')} className="font-mono text-sm w-full" placeholder="GSTIN" /></FormField>
                <FormField label="State">
                  <select value={form.state_id || ''} onChange={e => setForm(p => ({ ...p, state_id: e.target.value || null }))}
                    className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">— State —</option>
                    {states.map(s => <option key={s.state_id} value={s.state_id}>{s.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Pincode"><Input {...f('pincode')} placeholder="110001" /></FormField>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="GST Rate (%)" hint="e.g. 5 for 5% GST on sugar">
                  <Input type="number" step="0.01" min="0" max="100" {...f('gst_rate', 0)}
                    placeholder="5.00" className="text-sm w-full" />
                </FormField>
                <FormField label="Insurance per Qtl (₹)" hint="e.g. 2.75 per quintal">
                  <Input type="number" step="0.01" min="0" {...f('insurance_rate', 0)}
                    placeholder="2.75" className="text-sm w-full" />
                </FormField>
              </div>
              <FormField label="DO Narration (top)">
                <textarea value={form.do_narration || ''} onChange={e => setForm(p => ({ ...p, do_narration: e.target.value }))}
                  rows={2} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
              </FormField>
              <FormField label="Bottom Narration">
                <textarea value={form.bottom_narration || ''} onChange={e => setForm(p => ({ ...p, bottom_narration: e.target.value }))}
                  rows={2} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
              </FormField>
              <FormField label="Sale Order Emails" hint="CC on collective sale order mails — type email and press Enter">
                <EmailTagInput
                  value={form.sale_order_mail_list || []}
                  onChange={v => setForm(p => ({ ...p, sale_order_mail_list: v }))}
                  placeholder="sales@mill.com"
                />
              </FormField>
              <FormField label="Credit Sales Emails" hint="CC on collective credit mails — type email and press Enter">
                <EmailTagInput
                  value={form.credit_sales_list || []}
                  onChange={v => setForm(p => ({ ...p, credit_sales_list: v }))}
                  placeholder="credit@mill.com"
                />
              </FormField>
              <FormField label="Payment Undertaking Emails" hint="CC on collective undertaking mails — type email and press Enter">
                <EmailTagInput
                  value={form.payment_undertaking_mail_list || []}
                  onChange={v => setForm(p => ({ ...p, payment_undertaking_mail_list: v }))}
                  placeholder="finance@mill.com"
                />
              </FormField>
              <FormField label="Mill Employees" hint="These receive every DO email sent to this mill">
                <EmployeeListInput
                  value={form.mill_employees || []}
                  onChange={v => setForm(p => ({ ...p, mill_employees: v }))}
                />
              </FormField>
            </div>
          )}

          {modal.type === 'party' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Party Name *"><Input {...f('name')} /></FormField>
                <FormField label="Nickname"><Input {...f('nickname')} placeholder="Short name" /></FormField>
              </div>
              <FormField label="Address">
                <textarea value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))}
                  rows={2} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none" />
              </FormField>
              <FormField label="GSTIN"><Input {...f('gst')} className="font-mono text-sm w-full" placeholder="07AABCI1234F1Z5" /></FormField>
              <div className="grid grid-cols-3 gap-3">
                <FormField label="State">
                  <select value={form.state_id || ''} onChange={e => setForm(p => ({ ...p, state_id: e.target.value || null }))}
                    className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white w-full focus:outline-none focus:ring-1 focus:ring-blue-500">
                    <option value="">— Select State —</option>
                    {states.map(s => <option key={s.state_id} value={s.state_id}>{s.name}</option>)}
                  </select>
                </FormField>
                <FormField label="District"><Input {...f('district')} placeholder="e.g. Gurugram" /></FormField>
                <FormField label="Pincode"><Input {...f('pincode')} placeholder="110001" /></FormField>
              </div>
              <FormField label="194Q Status" hint="Section 194Q — Tax deducted at source on purchase of goods">
                <div className="flex items-center gap-4 mt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="is_194q"
                      checked={form.is_194q === true}
                      onChange={() => setForm(p => ({ ...p, is_194q: true }))}
                      className="w-4 h-4 accent-blue-600"
                    />
                    <span className="text-sm font-medium text-slate-700">YES</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="is_194q"
                      checked={!form.is_194q}
                      onChange={() => setForm(p => ({ ...p, is_194q: false }))}
                      className="w-4 h-4 accent-blue-600"
                    />
                    <span className="text-sm font-medium text-slate-700">N.A.</span>
                  </label>
                </div>
              </FormField>
              <FormField label="Phone Numbers (up to 3)">
                <div className="space-y-2">
                  {[0, 1, 2].map(idx => (
                    <Input key={idx} type="tel" placeholder={`Phone ${idx + 1}`}
                      value={(form.phone_numbers || [])[idx] || ''}
                      onChange={e => {
                        const phones = [...(form.phone_numbers || ['', '', ''])];
                        phones[idx] = e.target.value;
                        setForm(p => ({ ...p, phone_numbers: phones }));
                      }}
                      className="text-sm w-full" />
                  ))}
                </div>
              </FormField>
            </div>
          )}

          {modal.type === 'truck' && (
            <>
              <FormField label="Reg. No."><Input {...f('reg_no')} className="font-mono text-sm w-full uppercase" placeholder="HR26AK1234" /></FormField>
              <FormField label="Driver Name"><Input {...f('driver_name')} /></FormField>
            </>
          )}

          {modal.type === 'grade' && (
            <>
              <FormField label="Short Name (shown in forms & dropdowns)">
                <Input {...f('short_name')} placeholder="e.g. M30, S30" />
              </FormField>
              <FormField label="Full Name (shown on PDF & DO)">
                <Input {...f('name')} placeholder="e.g. M30 Grade Cement, S30 Special Grade" />
              </FormField>
            </>
          )}

          {modal.type === 'prod_year' && (
            <>
              <FormField label="S.No."><Input type="number" {...f('sno', 0)} /></FormField>
              <FormField label="Production Year *">
                <Input {...f('prod_year')} placeholder="2025-2026" />
                <p className="text-xs text-slate-400 mt-1">Format: YYYY-YYYY</p>
              </FormField>
            </>
          )}
        </Modal>
      )}

      {/* Mill Grades Modal */}
      {millGradesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Grades — {millGradesModal.mill.name}</h3>
              <button onClick={() => setMillGradesModal(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="px-5 py-4">
              <div className="flex flex-wrap gap-2">
                {grades.map(g => {
                  const selected = millGradesModal.selected.includes(g.grade_id);
                  const label = g.short_name ? `${g.short_name} — ${g.name}` : g.name;
                  return (
                    <button key={g.grade_id}
                      onClick={() => setMillGradesModal(prev => ({
                        ...prev,
                        selected: selected ? prev.selected.filter(id => id !== g.grade_id) : [...prev.selected, g.grade_id]
                      }))}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors
                        ${selected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}
                      data-testid={`grade-tag-${g.grade_id}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <Button variant="outline" size="sm" onClick={() => setMillGradesModal(null)}>Cancel</Button>
              <Button size="sm" onClick={saveMillGrades} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">
                {saving ? 'Saving...' : 'Save Grades'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mill Production Years Assignment Modal */}
      {millProdYearsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <h3 className="text-sm font-semibold text-slate-800">
                Production Years — <span className="text-blue-700">{millProdYearsModal.name}</span>
              </h3>
              <button onClick={() => setMillProdYearsModal(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="p-5 flex-1 overflow-y-auto">
              {prodYears.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center">No production years defined. Add them in the Prod. Years tab first.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 mb-3">Check the years available for this mill. Mark one as default (auto-selected in the billing form).</p>
                  {prodYears.map(py => {
                    const isChecked = millPySelected.has(py.prod_year_id);
                    return (
                      <div key={py.prod_year_id} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50">
                        <label className="flex items-center gap-3 cursor-pointer flex-1">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              setMillPySelected(prev => {
                                const next = new Set(prev);
                                if (next.has(py.prod_year_id)) {
                                  next.delete(py.prod_year_id);
                                  if (millPyDefault === py.prod_year_id) setMillPyDefault('');
                                } else { next.add(py.prod_year_id); }
                                return next;
                              });
                            }}
                            className="w-4 h-4"
                            data-testid={`mill-py-check-${py.prod_year_id}`}
                          />
                          <span className="text-sm text-slate-700 font-medium">{py.prod_year}</span>
                        </label>
                        {isChecked && (
                          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 ml-3">
                            <input
                              type="radio"
                              name="mill-py-default"
                              checked={millPyDefault === py.prod_year_id}
                              onChange={() => setMillPyDefault(py.prod_year_id)}
                              className="w-3.5 h-3.5"
                              data-testid={`mill-py-default-${py.prod_year_id}`}
                            />
                            Default
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-100 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setMillProdYearsModal(null)}>Cancel</Button>
              <Button size="sm" onClick={saveMillProdYears} disabled={millPySaving} className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-mill-prod-years-btn">
                {millPySaving ? 'Saving...' : <><Save size={12} className="mr-1" /> Save</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {userModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
              <h3 className="text-sm font-semibold text-slate-800">Add New User</h3>
              <button onClick={() => setUserModal(false)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              {userModalError && <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">{userModalError}</div>}
              <FormField label="Full Name"><Input placeholder="Rahul Sharma" value={userForm.name} onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} className="text-sm w-full" data-testid="new-user-name-input" /></FormField>
              <FormField label="Email *"><Input type="email" value={userForm.email} onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} className="text-sm w-full" data-testid="new-user-email-input" /></FormField>
              <FormField label="Role">
                <select value={userForm.role} onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}
                  className="border border-slate-200 rounded-md px-3 py-2 text-sm bg-white w-full" data-testid="new-user-role-select">
                  <option value="BROKER">Broker</option>
                  <option value="ADMIN">Admin</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                </select>
              </FormField>
              <FormField label="Password">
                <div className="relative">
                  <Input type={showNewPw ? 'text' : 'password'} placeholder="Min 6 characters" value={userForm.password}
                    onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))} className="text-sm w-full pr-10" data-testid="new-user-password-input" />
                  <button type="button" onClick={() => setShowNewPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNewPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </FormField>
              <div>
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 block">Phone Numbers (up to 5)</Label>
                <div className="space-y-2">
                  {userForm.phone_numbers.map((ph, idx) => (
                    <Input key={`new-user-phone-${idx}`} type="tel" placeholder={`Phone ${idx + 1}`} value={ph}
                      onChange={e => setUserForm(f => ({ ...f, phone_numbers: f.phone_numbers.map((p, i) => i === idx ? e.target.value : p) }))}
                      className="text-sm w-full" data-testid={`new-user-phone-${idx}`} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t sticky bottom-0 bg-white">
              <Button variant="outline" size="sm" onClick={() => setUserModal(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAddUser} disabled={savingUser} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-user-btn">
                {savingUser ? 'Adding...' : 'Add User'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Phone Numbers Modal */}
      {phoneModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Phone Numbers — {phoneModal.name}</h3>
              <button onClick={() => setPhoneModal(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-2">
              {phoneModal.phone_numbers.map((ph, idx) => (
                <Input key={`edit-phone-${idx}`} type="tel" placeholder={`Phone ${idx + 1}`} value={ph}
                  onChange={e => setPhoneModal(m => ({ ...m, phone_numbers: m.phone_numbers.map((p, i) => i === idx ? e.target.value : p) }))}
                  className="text-sm w-full" data-testid={`edit-phone-${idx}`} />
              ))}
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t">
              <Button variant="outline" size="sm" onClick={() => setPhoneModal(null)}>Cancel</Button>
              <Button size="sm" onClick={handleSavePhones} disabled={phoneSaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-phones-btn">
                {phoneSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Set Password Modal */}
      {pwModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Set Password — {pwModal.name}</h3>
              <button onClick={() => setPwModal(null)} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              {pwError && <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 text-sm text-red-700">{pwError}</div>}
              <FormField label="New Password">
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} placeholder="Min 6 characters" value={pwValue}
                    onChange={e => setPwValue(e.target.value)} className="text-sm w-full pr-10" data-testid="set-pw-input" autoFocus />
                  <button type="button" onClick={() => setShowPw(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </FormField>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t">
              <Button variant="outline" size="sm" onClick={() => setPwModal(null)}>Cancel</Button>
              <Button size="sm" onClick={handleSetPassword} disabled={pwSaving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-pw-btn">
                {pwSaving ? 'Saving...' : 'Set Password'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
